import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, PenLine, Mail } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { SiteFooter } from "@/components/SiteFooter";
import { IDS } from "@/lib/testIds";

const STEPS = [
  {
    step: "01 — Understand the scope",
    icon: ShieldCheck,
    title: "Invited and bounded",
    body: "This is a small exploratory discovery focused on one clear proposition, not a broad audit.",
  },
  {
    step: "02 — Share three public details",
    icon: PenLine,
    title: "A short intake",
    body: "Provide a LinkedIn profile, organisation website and email address for delivery of the report.",
  },
  {
    step: "03 — Receive the initial mapping",
    icon: Mail,
    title: "Prepared manually",
    body: "The KATLAS team reviews the public information and prepares an exploratory report for discussion.",
  },
];

export default function Landing() {
  return (
    <div data-testid={IDS.landingRoot} className="min-h-screen bg-stone-base paper-grain">
      <header className="mx-auto max-w-4xl px-6 pt-10">
        <BrandMark />
      </header>

      <main className="mx-auto max-w-4xl px-6 pb-16 pt-12 sm:pt-16">
        {/* Hero + explanation + primary action */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-teal-deep">
            KATLAS Cierge Design Studio
          </span>
          <h1 className="mt-4 max-w-3xl font-serif text-[42px] leading-[1.08] text-ink sm:text-[54px]">
            An invited discovery, prepared with care
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-ink-soft">
            KATLAS Cierge Design Studio runs small, invited discovery tests. We
            explore a founder’s current proposition, where trust and control may
            matter as they scale, which existing infrastructure should remain in
            place, and one bounded opportunity worth prototyping.
          </p>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted">
            This is an early testing environment. The first report is prepared
            manually — not an automated SaaS service — and no payment, account or
            password is required.
          </p>

          {/* Primary action — visible without scrolling */}
          <div className="mt-8">
            <Link
              to="/start"
              data-testid={IDS.landingPrimaryCta}
              className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-teal-deep"
            >
              Start your discovery
              <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-3 max-w-md text-[13.5px] leading-relaxed text-muted">
              It takes about two minutes to share the three public details needed
              for your initial report.
            </p>
          </div>
        </motion.div>

        {/* The pathway — three numbered journey cards */}
        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          {STEPS.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={`journey-card-${i + 1}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-xl border border-muted-line bg-stone-soft p-5"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-deep">
                  {p.step}
                </span>
                <p.icon className="h-5 w-5 text-teal" strokeWidth={1.8} />
              </div>
              <h3 className="mt-4 font-serif text-[18px] text-ink">{p.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{p.body}</p>
            </motion.div>
          ))}
        </div>

        {/* Pathway CTA — immediately after the cards */}
        <div className="mt-8 rounded-2xl border border-muted-line bg-white/70 p-6 sm:p-8">
          <h2 className="font-serif text-[22px] text-ink">Ready to share your details?</h2>
          <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-ink-soft">
            Continue to the short intake. No account, password or payment is required.
          </p>
          <Link
            to="/start"
            data-testid={IDS.landingCta}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-[15px] font-medium text-stone-soft transition-colors hover:bg-ink-deep"
          >
            Continue to the three-field intake
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* About this studio */}
        <section className="mt-14 border-t border-muted-line/70 pt-10">
          <h2 className="font-serif text-[22px] text-ink">About this studio</h2>
          <p className="mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted">
            KATLAS provides runtime operational-governance infrastructure. Cierge
            Design Studio is its applied discovery and pilot studio, running small
            invited explorations before anything is automated. The current
            walkthrough is prepared manually so we can test the questions,
            explanation and report with invited participants first.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
